/**
 * Mathematics Student Notes - Years 11-13
 * Based on Vanuatu National Syllabus
 * Comprehensive study materials for all strands
 */

// Year 11 Mathematics Student Notes
export const year11MathematicsStudentNotes = {
  strand1: {
    title: "Algebra",
    overview: "Foundation algebraic skills including expansion, factorization, surds, and solving equations",
    image: "https://images.unsplash.com/photo-1635070041078-e363dbe005cb?w=400",
    units: [
      {
        id: "unit1.1",
        title: "Expansion and Algebraic Expressions",
        content: `
## Algebraic Expressions

### Key Definitions
- **Algebraic term**: A combination of numbers and variables (e.g., 3x, 5y²)
- **Algebraic expression**: A combination of terms (e.g., 3x + 5y - 2)
- **Like terms**: Terms with the same variable(s) raised to the same power

### Laws of Indices
| Rule | Example |
|------|---------|
| aᵐ × aⁿ = aᵐ⁺ⁿ | x³ × x² = x⁵ |
| aᵐ ÷ aⁿ = aᵐ⁻ⁿ | x⁵ ÷ x² = x³ |
| (aᵐ)ⁿ = aᵐⁿ | (x²)³ = x⁶ |
| a⁰ = 1 | x⁰ = 1 |
| a⁻ⁿ = 1/aⁿ | x⁻² = 1/x² |

### Special Products
**Perfect Square:**
- (a + b)² = a² + 2ab + b²
- (a - b)² = a² - 2ab + b²

**Difference of Two Squares:**
- (a + b)(a - b) = a² - b²

### Distributive Law
a(b + c) = ab + ac

**Example:** Expand 3x(2x + 5)
= 3x × 2x + 3x × 5
= 6x² + 15x
`,
        keyTerms: [
          { term: "Coefficient", definition: "The number in front of a variable (e.g., 3 in 3x)" },
          { term: "Variable", definition: "A letter representing an unknown value (e.g., x, y)" },
          { term: "Term", definition: "A single mathematical expression (e.g., 3x, 5)" },
          { term: "Expression", definition: "A combination of terms (e.g., 3x + 5)" },
          { term: "Indices", definition: "Powers or exponents (e.g., x²)" },
          { term: "Expansion", definition: "Multiplying out brackets" }
        ],
        practiceQuestions: [
          "Expand (x + 3)²",
          "Simplify x³ × x⁴",
          "Expand (2x - 5)(2x + 5)"
        ],
        examTips: ["Always show working when expanding brackets", "Check by substituting a value"]
      },
      {
        id: "unit1.2",
        title: "Factorization",
        content: `
## Factorization Methods

### 1. Common Factor
Look for the highest common factor (HCF) of all terms.

**Example:** Factorise 6x² + 12x
= 6x(x + 2)

### 2. Grouping in Pairs
When there are 4 terms, group into pairs and factor each pair.

**Example:** Factorise ax + ay + bx + by
= a(x + y) + b(x + y)
= (x + y)(a + b)

### 3. Difference of Two Squares
a² - b² = (a + b)(a - b)

**Example:** Factorise x² - 16
= (x + 4)(x - 4)

### 4. Perfect Squares
- a² + 2ab + b² = (a + b)²
- a² - 2ab + b² = (a - b)²

### 5. Quadratic Trinomials (where a = 1)
x² + bx + c = (x + p)(x + q)
where p + q = b and p × q = c

**Example:** Factorise x² + 7x + 12
Find factors of 12 that add to 7: 3 and 4
= (x + 3)(x + 4)
`,
        keyTerms: [
          { term: "Factor", definition: "A number or expression that divides exactly into another" },
          { term: "HCF", definition: "Highest Common Factor - the largest factor shared by expressions" },
          { term: "Trinomial", definition: "An expression with three terms (e.g., x² + 5x + 6)" },
          { term: "Perfect Square", definition: "A number or expression that is the square of an integer or expression" },
          { term: "Difference of Squares", definition: "Expression in the form a² - b² = (a+b)(a-b)" }
        ],
        practiceQuestions: [
          "Factorise x² - 9",
          "Factorise x² + 5x + 6",
          "Factorise 3x² + 6x"
        ],
        examTips: ["Always check your answer by expanding the factors back"]
      },
      {
        id: "unit1.3",
        title: "Surds",
        content: `
## Square Roots and Surds

### Definition
A **surd** is a root that cannot be simplified to a whole number.
Examples: √2, √3, √5 are surds; √4 = 2 is NOT a surd.

### Simplifying Surds
√(ab) = √a × √b

**Example:** Simplify √20
= √(4 × 5)
= √4 × √5
= 2√5

### Operations with Surds

**Adding/Subtracting:** Only combine like surds
3√2 + 5√2 = 8√2
3√2 + 5√3 cannot be simplified

**Multiplying:**
√a × √b = √(ab)
2√3 × 4√5 = 8√15

**Dividing:**
√a ÷ √b = √(a/b)

### Rationalizing the Denominator
To remove a surd from the denominator, multiply by the conjugate.

**Example:** Simplify 5/√3
= (5 × √3)/(√3 × √3)
= 5√3/3
`,
        keyTerms: [
          { term: "Surd", definition: "A root that cannot be simplified to a rational number (e.g., √2)" },
          { term: "Rationalize", definition: "To remove a surd from the denominator of a fraction" },
          { term: "Conjugate", definition: "For a+√b, the conjugate is a-√b" },
          { term: "Simplify", definition: "To reduce an expression to its simplest form" },
          { term: "Irrational", definition: "A number that cannot be written as a simple fraction" }
        ],
        practiceQuestions: [
          "Simplify √50",
          "Simplify √3 × √12",
          "Rationalize 4/√2"
        ],
        examTips: ["Leave answers in surd form unless told otherwise", "Always simplify surds"]
      }
    ]
  },
  strand2: {
    title: "Geometry and Trigonometry",
    overview: "Triangle and circle geometry, trigonometric ratios and graphs, coordinate geometry",
    image: "https://images.unsplash.com/photo-1509228468518-180dd4864904?w=400",
    units: [
      {
        id: "unit2.1",
        title: "Trigonometric Ratios",
        content: `
## Triangle Trigonometry

### SOH CAH TOA (Right-Angled Triangles)
For an angle θ in a right-angled triangle:

| Ratio | Formula |
|-------|---------|
| sin θ | Opposite / Hypotenuse |
| cos θ | Adjacent / Hypotenuse |
| tan θ | Opposite / Adjacent |

### Pythagoras' Theorem
a² + b² = c²
where c is the hypotenuse

### Sine Rule (Any Triangle)
a/sin A = b/sin B = c/sin C

Use when you know:
- Two angles and one side
- Two sides and an angle opposite one of them

### Cosine Rule (Any Triangle)
a² = b² + c² - 2bc cos A

Use when you know:
- Two sides and the included angle
- All three sides (to find an angle)

### Area of a Triangle
Area = ½ ab sin C

where a and b are two sides and C is the included angle.
`,
        keyTerms: [
          { term: "Sine", definition: "Ratio of opposite side to hypotenuse in a right triangle" },
          { term: "Cosine", definition: "Ratio of adjacent side to hypotenuse in a right triangle" },
          { term: "Tangent", definition: "Ratio of opposite side to adjacent side in a right triangle" },
          { term: "Hypotenuse", definition: "The longest side of a right triangle, opposite the right angle" },
          { term: "Opposite", definition: "The side opposite to the angle being considered" },
          { term: "Adjacent", definition: "The side next to the angle (not the hypotenuse)" }
        ],
        practiceQuestions: [
          "Find sin 30° (exact value)",
          "In a right triangle with opposite = 3 and hypotenuse = 5, find sin θ",
          "Find the hypotenuse if the other sides are 6 and 8"
        ],
        examTips: ["Draw and label diagrams", "Check your calculator is in the correct mode (degrees)"]
      },
      {
        id: "unit2.2",
        title: "Circle Geometry",
        content: `
## Circle Geometry

### Radian Measure
- π radians = 180°
- 2π radians = 360°

**Converting:**
- Degrees to radians: multiply by π/180
- Radians to degrees: multiply by 180/π

### Arc Length
s = rθ (where θ is in radians)

**Example:** Find the arc length of a sector with radius 5 cm and angle 1.2 radians.
s = 5 × 1.2 = 6 cm

### Area of a Sector
A = ½r²θ (where θ is in radians)

### Area of a Segment
Area of segment = Area of sector - Area of triangle
A = ½r²(θ - sin θ)
`,
        keyTerms: [
          { term: "Radian", definition: "Unit of angle measurement where π radians = 180°" },
          { term: "Arc", definition: "A portion of the circumference of a circle" },
          { term: "Sector", definition: "A region bounded by two radii and an arc" },
          { term: "Segment", definition: "A region bounded by a chord and an arc" },
          { term: "Central Angle", definition: "An angle formed at the center of a circle" }
        ],
        practiceQuestions: [
          "Convert 90° to radians",
          "Find arc length: r = 4, θ = π/3",
          "Convert π/4 radians to degrees"
        ],
        examTips: ["Know when to use degrees vs radians", "Arc and sector formulas use radians!"]
      }
    ]
  },
  strand3: {
    title: "Sequences, Series and Calculus",
    overview: "Number patterns, functions, graphs, and introduction to differentiation",
    image: "https://images.unsplash.com/photo-1596495578065-6e0763fa1178?w=400",
    units: [
      {
        id: "unit3.1",
        title: "Arithmetic and Geometric Sequences",
        content: `
## Sequences and Series

### Arithmetic Sequence
A sequence where each term differs by a constant (common difference, d).

**nth term:** aₙ = a₁ + (n-1)d

**Sum of n terms:** Sₙ = n/2(2a₁ + (n-1)d) or Sₙ = n/2(a₁ + aₙ)

**Example:** 3, 7, 11, 15, ... (d = 4)
a₁ = 3, d = 4
10th term = 3 + (10-1)×4 = 3 + 36 = 39

### Geometric Sequence
A sequence where each term is multiplied by a constant (common ratio, r).

**nth term:** aₙ = a₁ × rⁿ⁻¹

**Sum of n terms:** Sₙ = a₁(rⁿ - 1)/(r - 1) for r ≠ 1

**Example:** 2, 6, 18, 54, ... (r = 3)
a₁ = 2, r = 3
5th term = 2 × 3⁴ = 2 × 81 = 162
`,
        keyTerms: [
          { term: "Sequence", definition: "An ordered list of numbers following a pattern" },
          { term: "Series", definition: "The sum of terms in a sequence" },
          { term: "Common Difference", definition: "The constant difference between consecutive terms in an arithmetic sequence" },
          { term: "Common Ratio", definition: "The constant ratio between consecutive terms in a geometric sequence" },
          { term: "nth Term", definition: "The formula to find any term in a sequence" }
        ],
        practiceQuestions: [
          "Find the 8th term: 5, 8, 11, 14, ...",
          "Find the 5th term: 2, 10, 50, ...",
          "Find the sum of first 10 terms: 3, 5, 7, 9, ..."
        ],
        examTips: ["Identify if the sequence is arithmetic (constant difference) or geometric (constant ratio)"]
      },
      {
        id: "unit3.2",
        title: "Introduction to Differentiation",
        content: `
## Differentiation Basics

### What is Differentiation?
Differentiation finds the rate of change or gradient of a function.

### Notation
If y = f(x), then the derivative is:
- dy/dx
- f'(x)
- y'

### Basic Rule
If f(x) = axⁿ, then f'(x) = naxⁿ⁻¹

**Examples:**
| f(x) | f'(x) |
|------|-------|
| x³ | 3x² |
| 5x² | 10x |
| 4x | 4 |
| 7 | 0 |

### Finding Gradient
The derivative gives the gradient at any point.

**Example:** Find the gradient of y = x² at x = 3
dy/dx = 2x
At x = 3: gradient = 2(3) = 6

### Stationary Points
At stationary points, dy/dx = 0

To find: Set derivative = 0 and solve for x.
`,
        keyTerms: [
          { term: "Derivative", definition: "The rate of change of a function at any point" },
          { term: "Gradient", definition: "The slope or steepness of a line or curve" },
          { term: "Rate of Change", definition: "How quickly one quantity changes relative to another" },
          { term: "Stationary Point", definition: "A point where the gradient equals zero" },
          { term: "First Principles", definition: "Deriving the derivative using the limit definition" }
        ],
        practiceQuestions: [
          "Differentiate y = x⁴",
          "Find dy/dx for y = 3x² + 2x - 5",
          "Find gradient of y = x³ at x = 2"
        ],
        examTips: ["Remember: the derivative of a constant is 0", "Show all working clearly"]
      }
    ]
  },
  strand4: {
    title: "Probability and Statistics",
    overview: "Probability calculations, statistical measures, and data representation",
    image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=400",
    units: [
      {
        id: "unit4.1",
        title: "Basic Probability",
        content: `
## Probability Fundamentals

### Key Terms
- **Event**: An outcome or set of outcomes
- **Sample Space**: All possible outcomes
- **Probability**: Likelihood of an event occurring

### Probability Formula
P(A) = Number of favorable outcomes / Total number of outcomes
P(A) = n(A) / n(S)

Probability always: 0 ≤ P(A) ≤ 1

### Complement of an Event
P(A') = 1 - P(A)

**Example:** If P(rain) = 0.3, then P(no rain) = 1 - 0.3 = 0.7

### Tree Diagrams
Used for sequential events. Multiply along branches.

**Example:** Two coin flips
           ┌── H (P = 1/4)
    ┌─ H ──┤
    │      └── T (P = 1/4)
    │      ┌── H (P = 1/4)
    └─ T ──┤
           └── T (P = 1/4)
`,
        keyTerms: [
          { term: "Probability", definition: "The likelihood of an event occurring, expressed as a number between 0 and 1" },
          { term: "Event", definition: "A specific outcome or set of outcomes from an experiment" },
          { term: "Outcome", definition: "A possible result of an experiment" },
          { term: "Sample Space", definition: "The set of all possible outcomes" },
          { term: "Complement", definition: "The probability that an event does NOT occur" }
        ],
        practiceQuestions: [
          "A bag has 3 red and 5 blue balls. Find P(red)",
          "If P(A) = 0.4, find P(A')",
          "A die is rolled. Find P(even number)"
        ],
        examTips: ["Draw tree diagrams for multi-stage events", "Always simplify fractions"]
      },
      {
        id: "unit4.2",
        title: "Statistical Measures",
        content: `
## Measures of Central Tendency

### Mean (Average)
Mean = Sum of all values / Number of values
x̄ = Σx / n

### Median
The middle value when data is arranged in order.
- Odd number of values: middle value
- Even number of values: average of two middle values

### Mode
The most frequently occurring value.

## Measures of Spread

### Range
Range = Highest value - Lowest value

### Quartiles
- Q1 (Lower quartile): 25th percentile
- Q2 (Median): 50th percentile
- Q3 (Upper quartile): 75th percentile
- IQR = Q3 - Q1

### Standard Deviation
Measures how spread out data is from the mean.
σ = √[Σ(x - x̄)² / n]

Lower SD = data closer to mean
Higher SD = data more spread out
`,
        keyTerms: [
          { term: "Mean", definition: "The average of a set of numbers (sum divided by count)" },
          { term: "Median", definition: "The middle value when data is arranged in order" },
          { term: "Mode", definition: "The most frequently occurring value in a dataset" },
          { term: "Range", definition: "The difference between the highest and lowest values" },
          { term: "Standard Deviation", definition: "A measure of how spread out data is from the mean" },
          { term: "Quartile", definition: "Values that divide data into four equal parts" }
        ],
        practiceQuestions: [
          "Find mean of 4, 7, 8, 11, 15",
          "Find median of 3, 5, 7, 9, 11",
          "Find range of 12, 5, 8, 20, 3"
        ],
        examTips: ["Always order data before finding median", "Show formula and substitution"]
      }
    ]
  }
};

// Year 12 Mathematics Student Notes
export const year12MathematicsStudentNotes = {
  strand1: {
    title: "Algebra",
    overview: "Advanced algebraic techniques including quadratic and cubic equations, rational expressions",
    image: "https://images.unsplash.com/photo-1635070041078-e363dbe005cb?w=400",
    units: [
      {
        id: "unit1.1",
        title: "Quadratic Equations and Graphs",
        content: `
## Quadratic Functions

### Standard Form
y = ax² + bx + c

- If a > 0, parabola opens upward (minimum)
- If a < 0, parabola opens downward (maximum)

### Key Features
- **Vertex (turning point):** x = -b/(2a)
- **Line of symmetry:** x = -b/(2a)
- **y-intercept:** (0, c)
- **x-intercepts:** solve ax² + bx + c = 0

### Solving Quadratic Equations

**1. Factorization:**
x² + 5x + 6 = 0
(x + 2)(x + 3) = 0
x = -2 or x = -3

**2. Quadratic Formula:**
x = (-b ± √(b² - 4ac)) / 2a

### Discriminant
Δ = b² - 4ac
- Δ > 0: Two real solutions
- Δ = 0: One real solution
- Δ < 0: No real solutions
`,
        keyTerms: [
          { term: "Quadratic", definition: "A polynomial of degree 2 (e.g., ax² + bx + c)" },
          { term: "Vertex", definition: "The turning point of a parabola" },
          { term: "Parabola", definition: "The U-shaped curve of a quadratic function" },
          { term: "Discriminant", definition: "b² - 4ac, determines the nature of roots" },
          { term: "Roots", definition: "The x-values where the function equals zero" }
        ],
        practiceQuestions: [
          "Solve x² - 5x + 6 = 0",
          "Find vertex of y = x² - 4x + 3",
          "Find discriminant of x² + 2x + 5 = 0"
        ],
        examTips: ["Use factorization when possible", "Use formula for harder questions"]
      }
    ]
  },
  strand3: {
    title: "Sequences, Series and Calculus",
    overview: "Sequences, differentiation, and introduction to integration",
    image: "https://images.unsplash.com/photo-1596495578065-6e0763fa1178?w=400",
    units: [
      {
        id: "unit3.3",
        title: "Integration",
        content: `
## Introduction to Integration

### Integration as Reverse of Differentiation
If dy/dx = axⁿ, then y = axⁿ⁺¹/(n+1) + c

**Basic Rule:**
∫axⁿ dx = axⁿ⁺¹/(n+1) + c (n ≠ -1)

### Examples
| Function | Integral |
|----------|----------|
| x² | x³/3 + c |
| 3x | 3x²/2 + c |
| 5 | 5x + c |

### Definite Integrals
∫ₐᵇ f(x) dx = F(b) - F(a)

**Example:** ∫₁³ 2x dx = [x²]₁³ = 9 - 1 = 8

### Area Under a Curve
Area = ∫ₐᵇ f(x) dx

For areas below x-axis, take absolute value.
`,
        keyTerms: [
          { term: "Integration", definition: "The reverse process of differentiation" },
          { term: "Antiderivative", definition: "A function whose derivative is the given function" },
          { term: "Definite Integral", definition: "An integral with upper and lower limits" },
          { term: "Area Under Curve", definition: "The region between a curve and the x-axis" }
        ],
        practiceQuestions: [
          "Integrate 3x²",
          "Find ∫₀² 4x dx",
          "Integrate 6x + 2"
        ],
        examTips: ["Don't forget +c for indefinite integrals", "Check by differentiating"]
      }
    ]
  }
};

// Year 13 Mathematics Student Notes
export const year13MathematicsStudentNotes = {
  strand2: {
    title: "Complex Numbers",
    overview: "Introduction to imaginary and complex numbers, operations, and representations",
    image: "https://images.unsplash.com/photo-1509228468518-180dd4864904?w=400",
    units: [
      {
        id: "unit2.1",
        title: "Complex Numbers",
        content: `
## Introduction to Complex Numbers

### The Imaginary Unit
i = √(-1), so i² = -1

Powers of i:
- i¹ = i
- i² = -1
- i³ = -i
- i⁴ = 1 (cycle repeats)

### Complex Number Form
z = a + bi (rectangular form)
- a = real part, Re(z)
- b = imaginary part, Im(z)

### Operations
**Addition:** (a + bi) + (c + di) = (a+c) + (b+d)i

**Multiplication:** (a + bi)(c + di) = (ac - bd) + (ad + bc)i

**Conjugate:** z̄ = a - bi

**Division:** Multiply by conjugate of denominator

### Modulus and Argument
**Modulus:** |z| = √(a² + b²)
**Argument:** θ = tan⁻¹(b/a)

### Polar Form
z = r(cos θ + i sin θ) = r cis θ
where r = |z|

### De Moivre's Theorem
(r cis θ)ⁿ = rⁿ cis(nθ)
`,
        keyTerms: [
          { term: "Imaginary", definition: "A number involving the square root of -1 (i)" },
          { term: "Complex", definition: "A number with both real and imaginary parts (a + bi)" },
          { term: "Modulus", definition: "The distance of a complex number from the origin" },
          { term: "Argument", definition: "The angle a complex number makes with the positive real axis" },
          { term: "Conjugate", definition: "For a + bi, the conjugate is a - bi" },
          { term: "Polar Form", definition: "Complex number expressed as r(cos θ + i sin θ)" }
        ],
        practiceQuestions: [
          "Simplify i⁵",
          "Find |3 + 4i|",
          "Find conjugate of 2 - 3i"
        ],
        examTips: ["Always simplify powers of i by finding remainder when dividing by 4"]
      }
    ]
  },
  strand4: {
    title: "Calculus",
    overview: "Advanced differentiation, integration techniques, and applications",
    image: "https://images.unsplash.com/photo-1596495578065-6e0763fa1178?w=400",
    units: [
      {
        id: "unit4.4",
        title: "Applications of Differentiation",
        content: `
## Kinematics (Motion Problems)

### Position, Velocity, Acceleration
If s(t) = position at time t:
- v(t) = ds/dt = velocity
- a(t) = dv/dt = d²s/dt² = acceleration

### Optimization Problems
1. Define the quantity to optimize
2. Express in terms of one variable
3. Differentiate and set = 0
4. Verify using second derivative

### Related Rates
Use chain rule: dy/dt = (dy/dx)(dx/dt)

### Implicit Differentiation
When y is not explicitly in terms of x:
Differentiate both sides, treating y as a function of x.

**Example:** x² + y² = 25
2x + 2y(dy/dx) = 0
dy/dx = -x/y
`,
        keyTerms: [
          { term: "Kinematics", definition: "The study of motion using calculus" },
          { term: "Optimization", definition: "Finding maximum or minimum values using derivatives" },
          { term: "Related Rates", definition: "Problems involving rates of change of related quantities" },
          { term: "Implicit Differentiation", definition: "Differentiating when y is not explicitly given as a function of x" }
        ],
        practiceQuestions: [
          "If s = t³ - 3t, find velocity at t = 2",
          "Find dy/dx if x² + y² = 16"
        ],
        examTips: ["Set up equations carefully in word problems", "Check units"]
      }
    ]
  }
};

export default year11MathematicsStudentNotes;
